import { Moment } from 'moment';

export const enum ExecutionSettingsType {
    Global,
    Robot,
}

export interface ISetting {
    id: string;
    name: string;
    value: string;
    scope?: string;
}

export interface IPasswordComplexity {
    hasDigits: boolean;
    hasLowerCase: boolean;
    hasSpecialCharacter: boolean;
    hasUpperCase: boolean;
    length: number;
    regex: {
        hasDigits: RegExp,
        hasLowerCase: RegExp,
        hasSpecialCharacter: RegExp,
        hasUpperCase: RegExp,
    };
}

export interface INugetDeploymentSettings {
    useSharedFeed?: boolean;
    isInternalDeployment: boolean;
    allowTenantPublish?: boolean;
    externalDeploymentUrl: string;
    selectedAuthenticationType: 'apiKey' | 'basic' | 'secure';
    apiKey: string;
    basicAuthUsername?: string;
    basicAuthPassword?: string;
}

export interface ISettingsMailSmtp {
    smtpHost: string;
    smtpPort: string;
    smtpDomain: string;
    smtpUsername: string;
    smtpPassword: string;
    smtpUseDefaultCredentials: boolean;
    smtpEnableSsl: boolean;
    defaultFromAddress: string;
    defaultFromDisplayName: string;
}

interface ISettingsMailAlert {
    alertsEmailEnabled: boolean;
}

interface ISettingsMail extends ISettingsMailSmtp, ISettingsMailAlert { }

export interface ISettingsScalability {
    signalR: {
        enabled: boolean;
        transportProtocol: {
            webSocketTransport: boolean;
            serverSentEventsTransport: boolean;
            longPollingTransport: boolean;
        }
    };
}

export interface ISettings {
    mail: ISettingsMail;
    general: {
        timezone: string;
        language: string;
        isModernFolderSystemEnabled: boolean;
    };
    deployment: {
        packages: INugetDeploymentSettings;
        libraries?: INugetDeploymentSettings;
    };
    security: {
        passwordComplexity: IPasswordComplexity;
        password: {
            defaultExpirationDays: number;
            previousUseLimit: number;
            shouldChangePasswordAfterFirstLogin: boolean;
        },
        lockout: {
            isEnabled: boolean;
            maxAccessAttempts: number;
            defaultAccountLockoutSeconds: number;
        },
        robot: {
            runDisconnected: number;
        }
    };
    scalability: ISettingsScalability;
    application: {
        isTenantRegistrationEnabled: boolean;
        termsAndConditionsRegisterUrl: string;
        customTitle: string;
    };
}

export enum CSVCalendarFields {
    ExcludedDate = 'ExcludedDate',
}

export interface ICalendarItemRow {
    excludedDate?: Moment;
}

export interface IExcludedCalendar {
    calendarId: number;
    calendarName: string;
    timeZoneId: string;
    excludedDates: Date[];
    newEntry?: boolean;
    loading?: boolean;
}

export interface ICSVCalendarItemRow {
    [key: string]: string | undefined;

    ExcludedDate?: string;
}

export interface IAuthConfig {
    serverSettings: IServerSettings;
    passwordComplexity: IPasswordComplexity;
    application: {
        isTenantRegistrationEnabled: boolean;
        termsAndConditionsRegisterUrl: string;
        isLocalizationEnabled: boolean;
    };
    mixpanelKey: string;
    isTelemetryEnabled: boolean;
    currentCulture: string;
    loginLayout: string;
    isRememberMeEnabled: boolean;
    cloud: {
        allowPasswordChange?: boolean;
        allowSelfEmailUpdate?: boolean;
        logoutUrl?: string;
    };
    auth: {
        isCustomSsoEnabled: boolean;
        isGoogleAuthenticationEnabled: boolean;
        isWindowsAuthenticationEnabled: boolean;
        isAzureAuthenticationEnabled: boolean;
        isSaml2AuthenticationEnabled: boolean;
        isSystemOpenIdAuthenticationEnabled: boolean;
    };
}

export interface IServerSettings {
    path: string;
    build: {
        version: string;
        date: Date;
    };
    baseTitle: string;
    year: number;
}
